/**
 * Manages a Web Worker for calculating player values in the background.
 * Handles initialization, message passing, error handling, and operation queuing.
 */
import { publishDebugLogEntries } from '../../helpers/DebugHelper.js';

export default class RankingsWorkerManager {
    constructor() {
        this.worker = null;
        this.pendingOperations = new Map();
        this.isReady = false;
    }

    /**
     * Initializes the worker if not already initialized.
     * Sets up message and error handlers.
     * @returns {Promise<boolean>} - A promise that resolves to true if the worker is
     * ready, false otherwise.
     */
    init() {
        // Return the same promise if already initializing
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = new Promise((resolve, _reject) => {
            if (this.worker) {
                resolve(true);
                return;
            }

            try {
                this.worker = new Worker(new URL('./../workers/RankingsWorker.js', import.meta.url), {
                    type: 'module',
                    name: 'rankings',
                });

                // Set up a one-time ready listener
                const readyHandler = e => {
                    if (e.data.type === 'ready') {
                        this.worker.removeEventListener('message', readyHandler);
                        this.isReady = true;
                        resolve(true);
                    }
                };

                // Set up error handler for initialization
                const errorHandler = error => {
                    this.worker.removeEventListener('error', errorHandler);
                    console.warn('Worker failed to initialize:', error);
                    this.isReady = false;
                    this.worker = null;
                    resolve(false); // Resolve with false instead of rejecting
                };

                this.worker.addEventListener('message', readyHandler);
                this.worker.addEventListener('error', errorHandler);

                // Set up the main message handlers after ready
                this.setupWorkerHandlers();

                // Timeout fallback in case worker never responds
                setTimeout(() => {
                    if (!this.isReady) {
                        console.warn('Worker initialization timeout');
                        this.terminate();
                        if (this.worker) {
                            this.worker.removeEventListener('message', readyHandler);
                            this.worker.removeEventListener('error', errorHandler);
                        }
                        resolve(false);
                    }
                }, 5000);
            } catch (error) {
                console.warn('Worker not supported or failed to create:', error);
                this.isReady = false;
                this.worker = null;
                resolve(false);
            }
        });

        return this.initPromise;
    }

    /**
     * Sets up message and error handlers for the worker.
     * Handles responses for pending operations and worker errors.
     * @private
     */
    setupWorkerHandlers() {
        this.worker.onmessage = e => {
            if (e.data.logEntries) {
                publishDebugLogEntries(e.data.logEntries);
            }

            const { type, id, result, error } = e.data;
            const operation = this.pendingOperations.get(id);

            if (!operation) return;

            if (type === 'success') {
                operation.resolve(result);
            } else {
                operation.reject(new Error(error));
            }

            this.pendingOperations.delete(id);
        };

        this.worker.onerror = err => {
            console.error('Worker error:', err);
            // Reject all pending operations
            this.pendingOperations.forEach(op => op.reject(err));
            this.pendingOperations.clear();
        };
    }

    /**
     * Executes an operation in the worker and returns a promise that resolves with the result.
     * @param {string} type - The type of operation to perform.
     * @param {object} data - The data to send to the worker.
     * @returns {Promise<any>} - A promise that resolves with the result of the operation
     * or rejects with an error.
     */
    async executeOperation(type, data) {
        if (!this.isReady) {
            throw new Error('Worker not ready');
        }
        const id = Math.random().toString(36).substring(2, 11);
        return new Promise((resolve, reject) => {
            this.pendingOperations.set(id, { resolve, reject });
            this.worker.postMessage({ type, data, id });
        });
    }

    /**
     * Terminates the worker and cleans up resources.
     * After calling this method, the manager will need to be re-initialized to use the worker again.
     */
    terminate() {
        this.isReady = false;
        if (this.worker) {
            this.worker.terminate();
        }
        this.worker = null;
    }
}
